import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Popover,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  AddTagPopupContainer,
  ChipStyled,
  SelectTagAutoComplete,
  classes,
} from './utils';
import tag from '../../../../assets/allInvoice-assets/tag.svg';
import {
  AssociateTagToAllDocuments,
  AssociateTagToSelectedDocuments,
  DeleteAssociateTagToSelectedDocuments,
  getTagData,
} from '../../../../services/tagApi';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import CustomTooltip from '../../utils/CustomTooltip';
import { DOCUMENT_ACTION_TOOLTIP } from '../../../../constants/tooltipConstant';

const OnselectAddTag = ({
  allRowSelected,
  selectedRow,
  onApiCompletion,
  gridApi,
  searchValue,
  customFilterValue,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [value, setValue] = useState([]);
  const [tags, setTags] = useState([]);
  // const [selectedValueId, setSelectedValueId] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const accessToken = getLocalStorageItem('AccessToken');

  let getTags;
  if (accessToken) {
    getTags = async () => {
      const res = await getTagData();
      if (res) {
        setTags(res);
      }
      setIsLoading(false);
    };
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setValue([]);
  };

  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
    onApiCompletion(value);
  };

  useEffect(() => {
    if (anchorEl !== null) {
      getTags();
    }
  }, [open]);

  useEffect(() => {
    if (anchorEl !== null) {
      const tagNames = selectedRow.flatMap((item) => item.associatedTags || []);
      const filterTag = new Map(tagNames.map((item) => [item.id, item]));
      setValue([...filterTag.values()]);
    }
  }, [selectedRow, anchorEl]);

  const associateTagToDocuments = async (selectedTagId, selectedDocumentId) => {
    try {
      return await AssociateTagToSelectedDocuments(
        selectedTagId,
        selectedDocumentId,
      );
    } catch (error) {
      return error;
    }
  };

  const DeleteTagToSelectedDocuments = async (
    selectedTagId,
    selectedDocumentId,
  ) => {
    try {
      return await DeleteAssociateTagToSelectedDocuments(
        selectedTagId,
        selectedDocumentId,
      );
    } catch (error) {
      return error;
    }
  };

  const associateTagToAllDocuments = async (
    search,
    filter,
    dropDownValue,
    tagId,
  ) => {
    try {
      gridApi?.showLoadingOverlay();
      const associatedTag = await AssociateTagToAllDocuments(
        search,
        filter,
        dropDownValue,
        tagId,
      );
      gridApi?.hideOverlay();
      return associatedTag;
    } catch (error) {
      return error;
    }
  };

  const handleAddValue = (newValue) => {
    // Determine the added tags (tags that are in newValue but not in the current value)
    const addedTags = newValue.filter(
      (item) => !value.some((val) => val.id === item.id),
    );

    // Determine the removed tags (tags that are in the current value but not in newValue)
    const removedTags = value.filter(
      (item) => !newValue.some((val) => val.id === item.id),
    );

    // Call AddTag API for the added tags
    if (addedTags.length > 0) {
      const addedTagIds = addedTags.map((item) => item.id);
      if (allRowSelected) {
        // If all rows are selected, associate tag to all documents
        const filterModel = gridApi.getFilterModel();
        associateTagToAllDocuments(
          searchValue,
          filterModel,
          customFilterValue,
          addedTagIds,
        );
      } else {
        // Associate tag to selected documents
        const documentIds = selectedRow.map((row) => row.id);
        associateTagToDocuments(addedTagIds, documentIds);
      }
    }

    // Call DeleteTag API for the removed tags
    if (removedTags.length > 0) {
      const removedTagIds = removedTags.map((item) => item.id);
      if (allRowSelected) {
        // If all rows are selected, delete the tag association from all documents
        const filterModel = gridApi.getFilterModel();
        associateTagToAllDocuments(
          searchValue,
          filterModel,
          customFilterValue,
          removedTagIds,
        );
      } else {
        // Delete tag association from selected documents
        const documentIds = selectedRow.map((row) => row.id);
        DeleteTagToSelectedDocuments(removedTagIds, documentIds);
      }
    }

    // Update the state with the new selected tag IDs
    setValue(newValue);
  };

  return (
    <>
      <CustomTooltip title={DOCUMENT_ACTION_TOOLTIP.ASSIGN_TAG}>
        <Box
          component="img"
          onClick={handleClick}
          src={tag}
          alt="tag"
          className={classes.actionIcons}
        />
      </CustomTooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <AddTagPopupContainer>
          <SelectTagAutoComplete
            multiple
            limitTags={2}
            value={value}
            isOptionEqualToValue={(option, newValue) =>
              option.name === newValue.name
            }
            ListboxProps={{ className: 'Autocomplete_scrollbar' }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                const modifiedEvent = { ...event };
                modifiedEvent.defaultMuiPrevented = true;
                handleClose();
              }
            }}
            onChange={(event, newValue) => {
              handleAddValue(newValue);
            }}
            disableCloseOnSelect
            freeSolo
            getOptionLabel={(option) => option.name}
            options={tags}
            renderOption={(props, option) => (
              <li {...props}>
                <Checkbox
                  checked={value.some((item) => item.name === option.name)}
                />
                {option.name}
              </li>
            )}
            renderTags={(tagValues, getTagProps) =>
              tagValues.map((option, index) => (
                <ChipStyled
                  key={`${index + 1}`}
                  label={option.name}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={isLoading ? 'Loading...' : 'Search Tags'}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </AddTagPopupContainer>
      </Popover>
    </>
  );
};

OnselectAddTag.defaultProps = {
  gridApi: null,
};

OnselectAddTag.propTypes = {
  selectedRow: PropTypes.arrayOf(
    PropTypes.shape({
      associatedTags: PropTypes.arrayOf(
        PropTypes.shape({
          created_at: PropTypes.string.isRequired,
          deleted_at: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.instanceOf(Date),
          ]),
          id: PropTypes.string.isRequired,
          organizations_id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          updated_at: PropTypes.string.isRequired,
        }),
      ),
      created_at: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      exported: PropTypes.string,
      invoice_id: PropTypes.string.isRequired,
      requested_by: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      updated_at: PropTypes.string.isRequired,
      uploaded: PropTypes.string.isRequired,
      validated: PropTypes.string,
    }),
  ).isRequired,
  allRowSelected: PropTypes.bool.isRequired,
  onApiCompletion: PropTypes.func.isRequired,
  searchValue: PropTypes.string.isRequired,
  gridApi: PropTypes.shape({
    getFilterModel: PropTypes.func.isRequired,
    showLoadingOverlay: PropTypes.func.isRequired,
    hideOverlay: PropTypes.func.isRequired,
  }),
  customFilterValue: PropTypes.shape({}).isRequired,
};

export default OnselectAddTag;
